import { GridMenu } from "@/grid/components/GridMenu";
import { MobileTeamCarousel } from "@/grid/components/MobileTeamCarousel";
import { SyncStatusPanel } from "@/grid/components/SyncStatusPanel";
import { TeamCard } from "@/grid/components/TeamCard";
import { Button } from "@/components/ui";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useGridData, useGridDepartment } from "@/grid/hooks/useGridData";
import { useDisplayGridData } from "@/grid/hooks/useDisplayGridData";
import type { GridTeam } from "@/grid/core/types";
import { calculateTeamRankings, sortTeamsByRank } from "@/grid/lib/scorecard";
import { parseUrlParamBoolean } from "@/grid/lib/display-params";
import { useIs2XL, useIsMobile } from "@/grid/hooks/use-mobile";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { cn } from "@/lib/cn";
import { format, formatDistanceToNow } from "date-fns";
import {
  AlertCircle,
  Calendar,
  DollarSign,
  Phone,
  RefreshCw,
  Send,
  Settings,
  Target,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { buildExportTeamData } from "@/grid/lib/export-snapshot";
import { getGridCellLayout } from "@/grid/lib/grid-cell-layout";

const iconMap: Record<string, LucideIcon> = {
  DollarSign,
  Target,
  Calendar,
  Phone,
  Send,
};

const SKELETON_COUNT = 6;
const STORAGE_KEY_LABELS = "warehouse-grid-showLabels";
const STORAGE_KEY_VALUES = "warehouse-grid-showValues";
const GRID_EXPORT_ELEMENT_ID = "grid-export-data";

type DisplayItem = { type: "skeleton"; index: number } | { type: "team"; team: GridTeam };

interface GridScorecardProps {
  departmentId: string;
  /** Token-gated display mode (TV / screenshot). No login required. */
  displayToken?: string;
}

function chunkIntoRows<T>(items: T[], cols: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += cols) {
    rows.push(items.slice(i, i + cols));
  }
  return rows;
}

function TeamCardSkeleton({
  periodCount,
  metricCount,
  showLabels,
  showValues,
  tvMode = false,
}: {
  periodCount: number;
  metricCount: number;
  showLabels: boolean;
  showValues: boolean;
  tvMode?: boolean;
}) {
  const layout = getGridCellLayout({ tvMode, showValues, showPeriodLabels: showLabels });

  return (
    <div
      className={cn("flex animate-pulse flex-col items-center rounded-xl", layout.cardClassName)}
    >
      <div className={cn("flex flex-col", layout.metricGap)}>
        {showLabels ? (
          <div className={cn("flex", layout.columnGap)}>
            {Array.from({ length: periodCount }).map((_, index) => (
              <div
                key={index}
                className={cn(layout.labelWidth, "h-3 rounded bg-[var(--color-muted)]")}
              />
            ))}
          </div>
        ) : null}
        {Array.from({ length: metricCount }).map((_, row) => (
          <div key={row} className={cn("flex", layout.columnGap)}>
            {Array.from({ length: periodCount }).map((_, col) => (
              <div
                key={col}
                className={cn(layout.cellSize, "rounded-md bg-[var(--color-muted)]")}
              />
            ))}
          </div>
        ))}
      </div>
      <div className={cn("h-4 w-24 rounded bg-[var(--color-muted)]", layout.footerMarginTop)} />
    </div>
  );
}

export function GridScorecard({ departmentId, displayToken }: GridScorecardProps) {
  const [searchParams] = useSearchParams();
  const isDisplayMode = Boolean(displayToken);
  const screenshotMode = searchParams.get("screenshot") === "1";
  const tvMode = isDisplayMode && parseUrlParamBoolean(searchParams.get("tv")) === true;
  const labelsParam = parseUrlParamBoolean(searchParams.get("labels"));
  const metricsParam = parseUrlParamBoolean(searchParams.get("metrics"));
  const labelsFromUrl = labelsParam !== null;
  const metricsFromUrl = metricsParam !== null;

  const department = useGridDepartment(departmentId);
  const authQuery = useGridData(departmentId, { enabled: !isDisplayMode });
  const displayQuery = useDisplayGridData(departmentId, displayToken ?? null);
  const activeQuery = isDisplayMode ? displayQuery : authQuery;
  const { data, isLoading, isError, refetch, isFetching, dataUpdatedAt, error } = activeQuery;
  const isMobile = useIsMobile();
  const is2XL = useIs2XL();
  const [, setTick] = useState(0);

  const [showLabels, setShowLabels] = useState(() => {
    if (labelsParam !== null) return labelsParam;
    const saved = localStorage.getItem(STORAGE_KEY_LABELS);
    return saved !== "false";
  });
  const [showValues, setShowValues] = useState(() => {
    if (metricsParam !== null) return metricsParam;
    const saved = localStorage.getItem(STORAGE_KEY_VALUES);
    return saved === "true";
  });

  useEffect(() => {
    if (labelsParam !== null) setShowLabels(labelsParam);
  }, [labelsParam]);

  useEffect(() => {
    if (metricsParam !== null) setShowValues(metricsParam);
  }, [metricsParam]);

  useEffect(() => {
    if (isDisplayMode && labelsFromUrl) return;
    localStorage.setItem(STORAGE_KEY_LABELS, String(showLabels));
  }, [showLabels, isDisplayMode, labelsFromUrl]);

  useEffect(() => {
    if (isDisplayMode && metricsFromUrl) return;
    localStorage.setItem(STORAGE_KEY_VALUES, String(showValues));
  }, [showValues, isDisplayMode, metricsFromUrl]);

  useEffect(() => {
    const interval = setInterval(() => setTick((value) => value + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  const teams = data?.teams ?? [];
  const rankings = useMemo(() => {
    if (!department?.showRanking || teams.length === 0) return undefined;
    return calculateTeamRankings(teams, department);
  }, [teams, department]);

  const sortedTeams = useMemo(() => {
    if (!rankings) return teams;
    return sortTeamsByRank(teams, rankings);
  }, [teams, rankings]);

  if (!department) {
    return (
      <div className="grid-bg flex min-h-dvh items-center justify-center p-6">
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Unknown department: {departmentId}
        </p>
      </div>
    );
  }

  const now = new Date();
  const todayFormatted = format(now, "EEEE, MMMM d");
  const todayFormattedShort = format(now, "EEE, MMM d");
  const columnCount = tvMode ? 3 : is2XL ? 6 : 3;
  const layout = getGridCellLayout({ tvMode, showValues, showPeriodLabels: showLabels });
  const rowGapClass = tvMode ? "gap-6" : "gap-6";
  // TV: keep legend in normal flow (centered with cards) so Puppeteer/Slack
  // screenshots cannot clip Calls / Opportunities at the left edge.
  const pageGapClass = tvMode ? "gap-5 px-10 py-5" : "gap-10 p-8";

  const rankingCount = department.rankingCount ?? 3;

  const displayItems: DisplayItem[] = isLoading
    ? Array.from({ length: SKELETON_COUNT }, (_, index) => ({ type: "skeleton", index }))
    : sortedTeams.map((team) => ({ type: "team", team }));

  const rows = chunkIntoRows(displayItems, isMobile ? 1 : columnCount);

  const loadErrorMessage =
    error instanceof Error ? error.message : isError ? "Unable to load team data" : null;

  const exportSnapshotJson = useMemo(() => {
    if (!screenshotMode || !data || isLoading || isError) return null;
    return JSON.stringify(buildExportTeamData(data));
  }, [screenshotMode, data, isLoading, isError]);

  const statusLine = (() => {
    const statusClass = "text-xs text-[var(--color-muted-foreground)]";
    if (isLoading) return <span className={cn(statusClass, "animate-pulse")}>Loading...</span>;
    if (isFetching) {
      return (
        <span className={cn(statusClass, "flex items-center gap-2")}>
          <RefreshCw className="h-3 w-3 animate-spin" />
          Refreshing
        </span>
      );
    }
    if (isError) {
      return (
        <button
          type="button"
          onClick={() => refetch()}
          className={cn("flex items-center gap-2 text-xs text-[var(--color-destructive)]")}
        >
          <AlertCircle className="h-3 w-3" />
          Retry
        </button>
      );
    }
    if (dataUpdatedAt) {
      return (
        <span className="flex items-center gap-2 text-xs text-[var(--color-muted-foreground)]">
          <span className="h-2 w-2 rounded-full bg-[var(--color-status-green)]" />
          {formatDistanceToNow(dataUpdatedAt, { addSuffix: true })}
        </span>
      );
    }
    return null;
  })();

  const controls = (
    <div className="flex items-center gap-4" data-export-ignore>
      <ToggleSwitch checked={showLabels} onChange={setShowLabels} label="Labels" />
      <ToggleSwitch checked={showValues} onChange={setShowValues} label="Metrics" />
      <button
        type="button"
        onClick={() => refetch()}
        disabled={isFetching || isLoading}
        aria-label="Refresh"
        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-muted-foreground)] transition-colors hover:bg-neutral-100 hover:text-[var(--color-foreground)] disabled:opacity-50"
      >
        <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
      </button>
    </div>
  );

  const mobileSettings = (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-11 w-11 p-0 text-[var(--color-muted-foreground)] -mr-2"
          aria-label="Grid settings"
        >
          <Settings className="h-5 w-5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" side="top" sideOffset={12} className="w-[calc(100vw-32px)] p-4">
        <div className="flex flex-col gap-1">
          <div className="flex min-h-[48px] items-center justify-between gap-4 rounded-lg px-2 -mx-2 active:bg-[var(--color-muted)]">
            <span className="text-base">Show Labels</span>
            <ToggleSwitch
              checked={showLabels}
              onChange={setShowLabels}
              label="Show Labels"
              className="[&>span:last-child]:sr-only"
            />
          </div>
          <div className="flex min-h-[48px] items-center justify-between gap-4 rounded-lg px-2 -mx-2 active:bg-[var(--color-muted)]">
            <span className="text-base">Show Metrics</span>
            <ToggleSwitch
              checked={showValues}
              onChange={setShowValues}
              label="Show Metrics"
              className="[&>span:last-child]:sr-only"
            />
          </div>
          <div className="mt-2 border-t border-[var(--color-border)] pt-3">
            <Button
              variant="secondary"
              onClick={() => refetch()}
              disabled={isFetching || isLoading}
              className="w-full justify-center gap-2"
            >
              <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
              {isFetching ? "Refreshing..." : "Refresh Data"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );

  const bottomBarClassName =
    "flex items-center gap-4 rounded-full border border-neutral-200/80 bg-white px-5 py-2.5 shadow-[0_2px_12px_rgba(0,0,0,0.08)]";

  const Legend = ({ rowIndex }: { rowIndex: number }) => (
    <div className={cn("flex items-start", layout.columnGap, layout.legendPaddingTop)}>
      <div className={cn("flex flex-col", layout.metricGap)}>
        {department.metrics.map((metric) => {
          const Icon = metric.icon ? iconMap[metric.icon] : null;
          return (
            <div
              key={`${metric.id}-${rowIndex}`}
              className={cn(
                "flex items-center justify-center text-[var(--color-muted-foreground)]/50",
                layout.legendRowHeight,
              )}
            >
              {Icon ? <Icon className={layout.legendIconClass} /> : null}
            </div>
          );
        })}
      </div>
      {showLabels ? (
        <div className={cn("flex flex-col", layout.metricGap)}>
          {department.metrics.map((metric) => (
            <div
              key={`${metric.id}-label-${rowIndex}`}
              className={cn(
                "flex items-center justify-start font-medium text-[var(--color-muted-foreground)]/50",
                layout.legendLabelClass,
                layout.legendRowHeight,
              )}
            >
              {metric.label}
            </div>
          ))}
        </div>
      ) : null}
      <div
        className={cn(
          "w-px self-stretch bg-[var(--color-border)]/50",
          layout.legendPaddingTop,
          tvMode ? "ml-1" : "ml-0.5",
        )}
      />
    </div>
  );

  if (isMobile && !isDisplayMode) {
    return (
      <div className="grid-bg h-adaptive-vh flex flex-col overflow-hidden">
        {exportSnapshotJson ? (
          <script
            type="application/json"
            id={GRID_EXPORT_ELEMENT_ID}
            dangerouslySetInnerHTML={{ __html: exportSnapshotJson }}
          />
        ) : null}
        <header className="standalone-safe-top relative shrink-0 border-b border-[var(--color-border)] bg-[var(--color-background)]">
          <div className="flex flex-col items-center gap-0 px-12 py-2">
            <h1 className="text-xl font-semibold text-[var(--color-foreground)]">
              {department.name}
            </h1>
            <span className="text-[11px] text-[var(--color-muted-foreground)]">
              {todayFormattedShort}
            </span>
          </div>
          <div className="absolute right-2 top-1 standalone-safe-top" data-export-ignore>
            <GridMenu className="h-11 w-11" />
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-2">
          {isError ? (
            <div className="flex flex-col items-center gap-4 py-12">
              <AlertCircle className="h-12 w-12 text-[var(--color-destructive)]/60" />
              <p className="text-center text-[var(--color-muted-foreground)]">
                Unable to load team data
              </p>
              <button type="button" onClick={() => refetch()} className="text-sm underline">
                Try again
              </button>
            </div>
          ) : !isLoading && sortedTeams.length === 0 ? (
            <p className="text-[var(--color-muted-foreground)]">No team data available</p>
          ) : (
            <MobileTeamCarousel
              teams={sortedTeams}
              department={department}
              isLoading={isLoading}
              showLabels={showLabels}
              showValues={showValues}
              rankings={rankings}
              rankingCount={rankingCount}
            />
          )}
        </main>

        <footer
          className="standalone-safe-bottom shrink-0 border-t border-[var(--color-border)] bg-[var(--color-background)]"
          data-export-ignore
        >
          <div className="flex items-center justify-between px-4 py-2">
            <div className="min-w-0">{statusLine}</div>
            {mobileSettings}
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid-bg relative flex min-h-dvh flex-col items-center justify-center",
        pageGapClass,
      )}
    >
      {exportSnapshotJson ? (
        <script
          type="application/json"
          id={GRID_EXPORT_ELEMENT_ID}
          dangerouslySetInnerHTML={{ __html: exportSnapshotJson }}
        />
      ) : null}
      {!isDisplayMode ? (
        <div className="fixed right-6 top-6 z-30" data-export-ignore>
          <GridMenu floating />
        </div>
      ) : null}

      <div className="flex flex-col items-center gap-1">
        <h1
          className={cn(
            "font-semibold text-[var(--color-foreground)]",
            tvMode ? "text-xl" : "text-xl",
          )}
        >
          {department.name}
        </h1>
        <span
          className={cn("text-[var(--color-muted-foreground)]", tvMode ? "text-sm" : "text-sm")}
        >
          {todayFormatted}
        </span>
      </div>

      {isError ? (
        <div className="flex flex-col items-center gap-4 py-12">
          <AlertCircle className="h-12 w-12 text-[var(--color-destructive)]/60" />
          <p className="max-w-md text-center text-[var(--color-muted-foreground)]">
            {loadErrorMessage ?? "Unable to load team data"}
          </p>
          <button type="button" onClick={() => refetch()} className="text-sm underline">
            Try again
          </button>
        </div>
      ) : displayItems.length === 0 ? (
        <p className="text-[var(--color-muted-foreground)]">No team data available</p>
      ) : (
        <div
          className={cn("flex flex-col items-center", tvMode ? "gap-5" : "gap-4")}
          data-grid-scorecard-rows
        >
          {rows.map((rowItems, rowIndex) => (
            <div
              key={rowIndex}
              className={cn("flex items-start", tvMode ? "justify-center" : "relative")}
            >
              <div
                className={cn(tvMode ? "shrink-0 pr-4" : "absolute right-full pr-2")}
                data-grid-legend
              >
                <Legend rowIndex={rowIndex} />
              </div>
              <div className={cn("flex", rowGapClass)}>
                {rowItems.map((item) =>
                  item.type === "skeleton" ? (
                    <TeamCardSkeleton
                      key={`skeleton-${item.index}`}
                      periodCount={department.periods.length}
                      metricCount={department.metrics.length}
                      showLabels={showLabels}
                      showValues={showValues}
                      tvMode={tvMode}
                    />
                  ) : (
                    <TeamCard
                      key={item.team.id}
                      team={item.team}
                      department={department}
                      showLabels={showLabels}
                      showValues={showValues}
                      tvMode={tvMode}
                      rank={rankings?.get(item.team.id)}
                      rankingCount={rankingCount}
                    />
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isDisplayMode ? (
        <div
          className={cn("fixed bottom-6 left-1/2 -translate-x-1/2", bottomBarClassName)}
          data-export-ignore
        >
          {statusLine}
          <div className="h-4 w-px bg-neutral-200" />
          <SyncStatusPanel />
          <div className="h-4 w-px bg-neutral-200" />
          {controls}
        </div>
      ) : null}
    </div>
  );
}
